import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { alertsAPI } from '../../services/api';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { fmtDate } from '../../utils/dateUtils';

const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const TABS = [
  { key: 'expired',  label: 'Expired',      color: 'red',    icon: '❌' },
  { key: 'critical', label: '≤ 30 Days',    color: 'orange', icon: '🔴' },
  { key: 'warning',  label: '≤ 60 Days',    color: 'yellow', icon: '🟡' },
  { key: 'notice',   label: '≤ 90 Days',    color: 'blue',   icon: '🔵' },
  { key: 'missing',  label: 'Missing Docs', color: 'gray',   icon: '⚠️' },
  { key: 'i983_due', label: 'I-983 Due',    color: 'violet', icon: '📋' },
];

const DOC_LABELS = {
  driving_license: 'Driving License',
  passport:        'Passport',
  ssn_card:        'SSN Card',
  visa_copy:       'Visa Copy',
  i20_certificate: 'I-20 Certificate',
  company_joining: 'Company Joining',
};

export default function AlertsPage() {
  const navigate = useNavigate();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [tab,     setTab]     = useState('expired');

  useEffect(() => {
    alertsAPI.list()
      .then((r) => setData(r.data.data))
      .catch(() => setError('Failed to load alerts.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner fullPage message="Loading alerts…" />;
  if (error)   return <div className="text-center py-16 text-red-600">{error}</div>;

  const { alerts, summary } = data;
  const activeItems = alerts[tab] || [];

  return (
    <div className="space-y-5">

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <SummaryCard label="Expired"     value={summary.total_expired}       color="red"    icon="❌" />
        <SummaryCard label="Expiring"    value={summary.total_expiring_soon} color="orange" icon="⏰" />
        <SummaryCard label="Missing"     value={summary.total_missing}       color="gray"   icon="⚠️" />
        <SummaryCard label="I-983 Due"   value={summary.total_i983_due ?? 0} color="violet" icon="📋" />
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {TABS.map(({ key, label, color, icon }) => {
            const count   = (alerts[key] || []).length;
            const isActive = tab === key;
            const tabColors = {
              red:    isActive ? 'border-red-500 text-red-700 bg-red-50'         : 'text-slate-500 hover:bg-slate-50',
              orange: isActive ? 'border-orange-500 text-orange-700 bg-orange-50': 'text-slate-500 hover:bg-slate-50',
              yellow: isActive ? 'border-yellow-500 text-yellow-700 bg-yellow-50': 'text-slate-500 hover:bg-slate-50',
              blue:   isActive ? 'border-blue-500 text-blue-700 bg-blue-50'      : 'text-slate-500 hover:bg-slate-50',
              gray:   isActive ? 'border-slate-500 text-slate-700 bg-slate-50'   : 'text-slate-500 hover:bg-slate-50',
              violet: isActive ? 'border-violet-500 text-violet-700 bg-violet-50': 'text-slate-500 hover:bg-slate-50',
            };

            return (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`
                  flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors
                  ${isActive ? tabColors[color] : 'border-transparent ' + tabColors[color]}
                `}
              >
                {icon} {label}
                {count > 0 && (
                  <span className={`
                    min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center
                    ${color === 'red'    ? 'bg-red-500 text-white'    :
                      color === 'orange' ? 'bg-orange-500 text-white' :
                      color === 'yellow' ? 'bg-yellow-500 text-white' :
                      color === 'blue'   ? 'bg-blue-500 text-white'   :
                      color === 'violet' ? 'bg-violet-500 text-white' :
                      'bg-slate-500 text-white'}
                  `}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        {activeItems.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <p className="text-3xl mb-2">✅</p>
            <p className="font-medium text-slate-600">No items in this category</p>
          </div>
        ) : tab === 'i983_due' ? (
          /* ── I-983 Due table ── */
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-violet-50 border-b border-violet-100">
                <tr>
                  {['Employee', 'Category', 'Report Month', 'Role / Client', 'Issues', 'Action'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {activeItems.map((item, i) => (
                  <tr key={i} className="hover:bg-violet-50/40 transition-colors">
                    <td className="px-4 py-3 font-medium text-slate-800">{item.employee_name}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs font-medium">
                        {item.category.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                      {MONTHS_FULL[item.report_month - 1]} {item.report_year}
                    </td>
                    <td className="px-4 py-3">
                      {(item.role || item.client) ? (
                        <div className="space-y-0.5">
                          {item.role   && <div className="text-slate-700 font-medium">{item.role}</div>}
                          {item.client && <div className="text-slate-500 text-xs">{item.client}</div>}
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      {item.issues ? (
                        <span className="text-orange-700 bg-orange-50 border border-orange-200 rounded px-2 py-0.5 text-xs leading-relaxed block">
                          {item.issues}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                      {item.description && (
                        <p className="text-slate-500 text-xs mt-1 truncate max-w-[200px]" title={item.description}>
                          {item.description}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/employees/${item.employee_id}`)}
                        className="text-violet-600 hover:text-violet-800 text-xs font-semibold bg-violet-50 border border-violet-200 px-3 py-1 rounded-lg hover:bg-violet-100">
                        Upload I-983 →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* ── Standard document alerts table ── */
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  {['Employee', 'Category', 'Document', 'Number', 'Expiry Date', 'Days', 'Action'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {activeItems.map((item, i) => (
                  <tr key={i} className="hoverable">
                    <td className="px-4 py-3 font-medium text-slate-800">{item.employee_name}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                        {item.category.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{DOC_LABELS[item.document_type] || item.document_type}</td>
                    <td className="px-4 py-3 text-slate-500">{item.document_number || '—'}</td>
                    <td className="px-4 py-3">
                      {item.expiry_date
                        ? <ExpiryBadge date={item.expiry_date} days={item.days_until_expiry} tab={tab} />
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {item.days_until_expiry !== null
                        ? <DaysBadge days={item.days_until_expiry} tab={tab} />
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/employees/${item.employee_id}`)}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium">
                        View →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color, icon }) {
  const bg = { red: 'bg-red-50 border-red-200', orange: 'bg-orange-50 border-orange-200', gray: 'bg-slate-50 border-slate-200', violet: 'bg-violet-50 border-violet-200' };
  const tx = { red: 'text-red-700', orange: 'text-orange-700', gray: 'text-slate-700', violet: 'text-violet-700' };
  return (
    <div className={`rounded-xl border p-5 ${bg[color]}`}>
      <div className="text-3xl mb-1">{icon}</div>
      <div className={`text-3xl font-bold ${tx[color]}`}>{value}</div>
      <div className={`text-sm font-medium mt-1 ${tx[color]} opacity-80`}>{label}</div>
    </div>
  );
}

function ExpiryBadge({ date, days, tab }) {
  const cls = tab === 'expired' || days < 0 ? 'text-red-600 font-semibold' :
              days <= 30 ? 'text-orange-600 font-medium' :
              days <= 60 ? 'text-yellow-600' : 'text-slate-600';
  return <span className={cls}>{fmtDate(date)}</span>;
}

function DaysBadge({ days, tab }) {
  if (tab === 'missing' || days === null) return <span className="text-slate-400">—</span>;
  if (days < 0)  return <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-bold">{Math.abs(days)} days ago</span>;
  if (days <= 30) return <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-bold">{days} days</span>;
  if (days <= 60) return <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-xs font-bold">{days} days</span>;
  return <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs">{days} days</span>;
}
